import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getVirtualAccounts,
  addVirtualAccount,
  updateVirtualAccount,
  deleteVirtualAccount,
  VirtualAccount
} from "./_virtualAccountAction";

interface VirtualAccountState {
  virtualAccounts: VirtualAccount[];
  isLoading: boolean;
  isDeleting: boolean;
  error: string | null;
  responseCode: number | null;
  totalCount: number;
  currentPage: number;
  totalPages: number;
}

const initialState: VirtualAccountState = {
  virtualAccounts: [],
  isLoading: false,
  isDeleting: false,
  error: null,
  responseCode: null,
  totalCount: 0,
  currentPage: 1,
  totalPages: 1,
};

const virtualAccountSlice = createSlice({
  name: "virtualAccount",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.responseCode = null;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Get Virtual Accounts
    builder
      .addCase(getVirtualAccounts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getVirtualAccounts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.virtualAccounts = action.payload.data || [];
        state.totalCount = action.payload.totalCount || 0;
        state.totalPages = action.payload.totalPages || 1;
      })
      .addCase(getVirtualAccounts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Add Virtual Account
    builder
      .addCase(addVirtualAccount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addVirtualAccount.fulfilled, (state, action) => {
        state.isLoading = false;
        state.responseCode = action.payload.responseCode;
        if (action.payload.data) {
          state.virtualAccounts.unshift(action.payload.data);
        }
      })
      .addCase(addVirtualAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update Virtual Account
    builder
      .addCase(updateVirtualAccount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateVirtualAccount.fulfilled, (state, action) => {
        state.isLoading = false;
        state.responseCode = action.payload.responseCode;
        if (action.payload.data) {
          const index = state.virtualAccounts.findIndex(
            account => account.id !== action.payload.data.id
          );
          if (index !== -1) {
            state.virtualAccounts[index] = action.payload.data;
          }
        }
      })
      .addCase(updateVirtualAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Delete Virtual Account
    builder
      .addCase(deleteVirtualAccount.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteVirtualAccount.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.virtualAccounts = state.virtualAccounts.filter(
          account => account.id !== action.payload
        );
      })
      .addCase(deleteVirtualAccount.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setCurrentPage } = virtualAccountSlice.actions;
export default virtualAccountSlice.reducer;
